/**
 * Utility for managing pending ownership addition state in sessionStorage.
 * Used when user clicks "+ Add additional borrower" in Property Ownership step
 * and needs to return to Borrower Details to add a new borrower.
 */

import type { PropertyOwnership } from '@/types';

export interface PendingOwnershipAddition {
  propertyId: string;
  existingBorrowerGroupIds: string[];
  /** Current ownership state to preserve across navigation */
  propertyOwnershipState?: PropertyOwnership[];
}

const STORAGE_KEY = 'pendingOwnershipAddition';

export const pendingOwnershipStorage = {
  /**
   * Store pending ownership data before navigating to Borrower Details
   */
  set(data: PendingOwnershipAddition): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // sessionStorage unavailable - silently ignore
    }
  },

  /**
   * Retrieve and parse pending ownership data
   * Returns null if no data exists or data is invalid
   */
  get(): PendingOwnershipAddition | null {
    try {
      const json = sessionStorage.getItem(STORAGE_KEY);
      if (!json) return null;
      return JSON.parse(json) as PendingOwnershipAddition;
    } catch {
      return null;
    }
  },

  /**
   * Remove pending ownership data from storage
   */
  remove(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // sessionStorage unavailable - silently ignore
    }
  },

  /**
   * Check if pending ownership data exists (without parsing)
   */
  exists(): boolean {
    try {
      return sessionStorage.getItem(STORAGE_KEY) !== null;
    } catch {
      return false;
    }
  },
};
